import { app } from "js/namespaces";
import { sanitizeHtmlText } from "./htmlTextHelpers";
import * as browser from "js/core/utilities/browser";

export const ClipboardType = {
    TEXT: "text/plain",
    HTML: "text/html",
    EDITOR_ELEMENT: "web application/bai-editor-element",
    ELEMENTS: "web application/bai-elements",
    BLOCKS: "web application/bai-blocks",
    CELLS: "web application/bai-cells",
    SLIDES: "web application/bai-slides",
    STYLES: "web application/bai-styles",
};

// Provide a mapping of mimeType:value.
//   This allows muliple formats to be written at once.
export const clipboardWrite = async mimeTypeMapping => {
    // Store a copy for synchronous queries elsewhere
    app.lastCopy = mimeTypeMapping;

    // Firefox doesn't support clipboard.write
    if (navigator.clipboard?.write && browser.isChrome) {
        let mapping = Object.entries(mimeTypeMapping)
            .reduce((result, [mimeType, value]) => {
                if (mimeType.includes("application/bai")) {
                    value = JSON.stringify(value);
                }

                let blobType = mimeType.replace("web ", "");
                let blob = new Blob(
                    [value],
                    { type: blobType },
                );

                result[mimeType] = blob;
                return result;
            }, {});

        let item = new window.ClipboardItem(mapping);
        await navigator.clipboard.write([item]);

        // TODO: brent: Use event.originalEvent.clipboardData || event.clipboardData as a fallback instead.

    // Just write text if we can't be more specific
    } else {
        let [
            mimeType,
            value,
        ] = Object.entries(mimeTypeMapping)[0];

        if (mimeType.includes("application/bai")) {
            value = `${mimeType}:${JSON.stringify(value)}`;
        }
        await navigator.clipboard.writeText(value);
    }
};

// Provide an array of acceptible mimeType matches.
//   First mimeType takes precedence.
export const clipboardRead = async (mimeTypes = []) => {
    let value;

    // Firefox doesn't support clipboard.read
    if (navigator.clipboard?.read && browser.isChrome) {
        const permission = navigator.permissions && await navigator.permissions.query({ name: "clipboard-read" });
        if (permission?.state === "denied") {
            console.error("Clipboard read denied.", permission);
            return value;
        }

        let items = await navigator.clipboard.read();
        let item = items.find(x => mimeTypes.some(mimeType => x.types.includes(mimeType)));
        if (item) {
            let mimeType = mimeTypes.find(type => item.types.includes(type));
            let blob = await item.getType(mimeType);
            let text = await blob.text();

            if (mimeType.includes("application/bai")) {
                value = JSON.parse(text);
            } else if (mimeType === ClipboardType.HTML) {
                value = sanitizeHtmlText(text);
            } else if (mimeType === ClipboardType.TEXT) {
                value = text;
            }
        }

        // TODO: brent: Use event.originalEvent.clipboardData || event.clipboardData as a fallback instead.

    // Just read text if we can't be more specific
    } else {
        let text = await navigator.clipboard.readText();

        // Look for the application mime type flag if we're looking for any application mime types
        let applicationMimeTypes = mimeTypes.filter(mimeType => mimeType.includes("application/bai"));
        let mimeType = applicationMimeTypes.find(mimeType => text.includes(`${mimeType}:`));
        if (mimeType) {
            value = JSON.parse(text.replace(`${mimeType}:`, ""));
        } else if (mimeTypes.includes(ClipboardType.HTML)) {
            value = sanitizeHtmlText(text);
        } else if (mimeTypes.includes(ClipboardType.TEXT)) {
            value = text;
        }
    }

    return value;
};
